import { FC, memo } from 'react';
import { css } from '@emotion/react';
import { UserDetails } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { IconName } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { PersonSearchableListbox } from '@frontend/task-center-components';
import { theme } from '@frontend/theme';
import { Avatar, PopoverDialog, Text, usePopoverDialog } from '@frontend/design-system';
import { TaskFormRow } from '../../common';

interface AssigneePickerProps {
  label: string;
  icon: IconName;
  buttonIconName: IconName;
  placeholder: string;
  onChange: (value: string) => void;
  value: string;
  isEditing?: boolean;
  isRemovable?: boolean;
  data: UserDetails[];
  disabled?: boolean;
  focused?: boolean;
  trackingIds?: {
    removeButton?: string;
    chipButton?: string;
  };
}

export const PersonPicker: FC<AssigneePickerProps> = memo(
  ({
    label,
    icon,
    buttonIconName,
    placeholder,
    onChange,
    data,
    value,
    trackingIds,
    isEditing,
    isRemovable,
    disabled = false,
  }) => {
    const { getDialogProps, getTriggerProps, close } = usePopoverDialog({ placement: 'bottom-start' });

    if (!data) {
      return null;
    }

    const personValue = data.find((item) => item.userId === value);
    const fullName = personValue ? getFullName(personValue) : '';

    const leftElement = fullName ? <Avatar size='xs' isUser name={fullName} /> : null;

    const onChangeValue = (value: string | string[]) => {
      const selected = Array.isArray(value) ? value[0] : value;
      if (selected) onChange(selected);
      onChange(Array.isArray(value) ? value[0] : value);
      close();
    };

    const renderItem = (name: string) => {
      return (
        <>
          <Avatar size='xs' name={name} isUser /> <Text size='large'>{name}</Text>
        </>
      );
    };

    return (
      <TaskFormRow
        label={label}
        icon={icon}
        placeholder={placeholder}
        buttonIconName={buttonIconName}
        value={fullName}
        getTriggerProps={getTriggerProps}
        trackingIds={trackingIds}
        leftElement={leftElement}
        isEditing={isEditing}
        isRemovable={isRemovable}
        onClickRemove={() => onChange('')}
        disabled={disabled}
      >
        <PopoverDialog {...getDialogProps()} css={popoverDialogStyle}>
          <PersonSearchableListbox<UserDetails>
            data={data as UserDetails[]}
            keyGen={(item) => `searchable-${item.userId}`}
            getItemValue={(item) => item.userId ?? ''}
            value={value}
            onSelect={onChangeValue}
            renderItem={(user) => {
              const name = getFullName(user);
              return renderItem(name);
            }}
            getSearchValue={(item) => getFullName(item)}
          />
        </PopoverDialog>
      </TaskFormRow>
    );
  }
);

PersonPicker.displayName = 'PersonPicker';

const popoverDialogStyle = css`
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.floating};
  border: 1px solid ${theme.colors.neutral10};
  width: 300px;
`;
